/**
 * Billing API — Layer 1 of the three-layer pattern.
 *
 * Pure async functions over the shared axios client. No TanStack, no React.
 * Backend sends/accepts camelCase; responses are used directly.
 */
import { apiClient } from "@/shared/lib/axios";
import { BILLING } from "@/shared/lib/apiPaths";
import type { AccessState, BillingPlan, SubscriptionState, CheckoutInput } from "../types";

/** GET /billing/plans — tier/interval price matrix (flat list). */
export async function getBillingPlans(): Promise<BillingPlan[]> {
  const { data } = await apiClient.get<{ plans: BillingPlan[] }>(`${BILLING}/plans`);
  return data.plans;
}

/** GET /billing/subscription — access state + the caller's subscription mirror. */
export async function getSubscription(): Promise<SubscriptionState> {
  const { data } = await apiClient.get<SubscriptionState>(`${BILLING}/subscription`);
  return data;
}

/** POST /billing/checkout — returns the hosted Checkout Session URL. */
export async function createCheckout(input: CheckoutInput): Promise<string> {
  const { data } = await apiClient.post<{ checkoutUrl: string }>(
    `${BILLING}/checkout`,
    input,
  );
  return data.checkoutUrl;
}

/**
 * POST /billing/checkout/confirm — reconcile a completed Checkout Session.
 *
 * Called from the success page so a paid publisher activates immediately,
 * without waiting on the async Stripe webhook. Returns the fresh access state.
 */
export async function confirmCheckout(sessionId: string): Promise<AccessState> {
  const { data } = await apiClient.post<{ accessState: AccessState }>(
    `${BILLING}/checkout/confirm`,
    { sessionId },
  );
  return data.accessState;
}

/** POST /billing/portal — returns the Stripe Billing Portal URL. */
export async function createPortalSession(): Promise<string> {
  const { data } = await apiClient.post<{ portalUrl: string }>(`${BILLING}/portal`);
  return data.portalUrl;
}
